'use client';

import { useEffect } from 'react';

/**
 * Route-segment error boundary (PAS-55). A WP-backed page render throws when the
 * backend is unreachable; for a path with a cached render Next keeps serving the
 * last good HTML and never reaches here, so this only renders for paths with no
 * cached version yet (a never-visited dynamic slug during an outage). It shows a
 * styled "temporarily unavailable" page instead of a blank shell or the raw
 * Next.js error screen.
 *
 * Kept intentionally self-contained: `error.tsx` is a Client Component and any
 * throw inside it bubbles past this boundary to `global-error` (which we don't
 * define) and blanks the page — so this uses only native elements, no imported
 * client components (e.g. the site Nav/Footer) that could throw during the error
 * render. Next does not put this fallback in the initial server HTML for a
 * hard-loaded SSR error; it renders on the client after hydration. Renders with
 * an HTTP 500 status (App Router can't set 503 from an error boundary).
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // The failing render already emitted a structured wp_fetch_failed line
    // server-side; surface the boundary catch in the browser console too.
    console.error(error);
  }, [error]);

  return (
    <main className="min-h-screen bg-blue flex items-center justify-center px-8">
      <div className="max-w-theme w-full py-24 text-center">
        <h1 className="font-bold text-xl md:text-2xl leading-[1.2] mb-6">
          Content temporarily unavailable
        </h1>
        <p className="mb-8 text-[16px]">
          We&rsquo;re having trouble loading this page right now. Please try
          again in a few minutes.
        </p>
        <div className="flex flex-wrap gap-6 justify-center text-[16px]">
          <a href="/" className="underline hover:no-underline">
            Return home
          </a>
          <button
            type="button"
            onClick={() => reset()}
            className="underline hover:no-underline"
          >
            Try again
          </button>
        </div>
      </div>
    </main>
  );
}
